import type { Injector, Signal } from '@angular/core'
import { computed, Injectable, signal, inject } from '@angular/core'
import { TaskStore } from '@awork/features/task/state/task.store'
import type { DateRange } from '@awork/_shared/functions/date-operations'
import { areDatesOverlapping } from '@awork/_shared/functions/date-operations'
import { addDays, differenceInDays, differenceInMinutes } from '@awork/_shared/functions/date-fns-wrappers'
import { Task, TASK_LIST_WIDE_THRESHOLD } from '@awork/features/task/models/task.model'
import type { Observable } from 'rxjs'
import { map } from 'rxjs'
import { UserQuery } from '@awork/features/user/state/user.query'
import type { User } from '@awork/features/user/models/user.model'
import type { ProjectStatusType } from '@awork/features/project/models/project-status.model'
import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import { Order } from '@awork/core/state/signal-store/types'
import { ProjectQuery } from '@awork/features/project/state/project.query'
import { taskSearchFilter } from '@awork/features/task/functions/task-query-helpers'

interface BaseFilterOptions {
  assigned?: boolean // True to select only assigned tasks
  nonDone?: boolean // True to filter out done tasks
  searchQuery?: string
  sortByOrder?: boolean // True to sort by task order
}

interface FilterOptions extends BaseFilterOptions {
  filterOldDoneTasks?: boolean // True to filter out done tasks older than 30 days
  includeExternal?: boolean // Includes tasks which are from connected projects that belong to another workspace
  excludeSubtasks?: boolean // Excludes subtasks
}

interface AssignedFilterOptions {
  assignedToOthers?: boolean // True to select private tasks assigned to others
  activeProjects?: boolean // True to select only project tasks from active projects (in progress)
  doneTasksSinceYesterday?: boolean // Includes done tasks which changed to done since yesterday
  includeOldDoneTasks?: boolean // Includes done tasks which changed to done since 30 days ago
  includeSubtasks?: boolean // Includes subtasks always if parent task is part of the filter, even when the subtask doesn't meet the filter criteria
  includeExternal?: boolean // Includes tasks which are from connected projects that belong to another workspace
}

export enum TaskColumn {
  TaskKey = 'taskKey',
  IsPrio = 'isPrio',
  Files = 'files',
  Subtasks = 'subtasks',
  ChecklistItems = 'checklistItems',
  Lists = 'lists',
  Projects = 'projects',
  Tags = 'tags',
  Recurrency = 'recurrency',
  TrackedDuration = 'trackedDuration',
  TotalTrackedDuration = 'totalTrackedDuration',
  PlannedEffort = 'plannedEffort',
  DueOn = 'dueOn',
  CommentCount = 'commentCount',
  Assignee = 'assignee'
}

@Injectable({ providedIn: 'root' })
export class TaskQuery extends EntitySignalQuery<Task> {
  protected store: TaskStore
  private userQuery = inject(UserQuery)
  private settingsQuery = inject(SettingsQuery)
  private browserService = inject(BrowserService)
  private projectQuery = inject(ProjectQuery)

  constructor() {
    const store = inject(TaskStore)

    super(store)

    this.store = store
  }

  /**
   * Query tasks by filter expression
   * @param {string} filterExpression
   * @returns {Signal<Task[]>}
   */
  queryTasksByFilterExpression(filterExpression: string): Signal<Task[]> {
    return this.queryAllByFilterExpression(filterExpression, {
      sortBy: 'name',
      sortByOrder: Order.ASC
    })
  }

  queryAssignedTasks(assignedFilterOptions: AssignedFilterOptions): Signal<Task[]> {
    const currentUser = this.userQuery.getCurrentUser()

    if (!currentUser) {
      return signal([])
    }

    return this.queryAll({
      filterBy: task => {
        task = new Task(task)

        return this.filterAssignedTasks(task, assignedFilterOptions)
      }
    })
  }

  /**
   * Returns true if a given task complies with the assigned filter options
   * @param {Task} task
   * @param {AssignedFilterOptions} [options={}]
   * @returns {boolean}
   */
  filterAssignedTasks(task: Task, options: AssignedFilterOptions = {}): boolean {
    const {
      assignedToOthers,
      activeProjects,
      doneTasksSinceYesterday,
      includeOldDoneTasks,
      includeSubtasks,
      includeExternal
    } = options
    const canSeeAllPrivateTasks = assignedToOthers && task.baseType === 'private'

    const currentUser = this.userQuery.queryCurrentUser()

    if (includeSubtasks && this.isParentTaskAssigned(task, options)) {
      return true
    }

    return (
      (canSeeAllPrivateTasks || task.isUserAssigned(currentUser()?.id)) &&
      (!doneTasksSinceYesterday || this.filterRecentDoneTasks(task)) &&
      (includeOldDoneTasks || this.filterOldDoneTasks(task)) &&
      (!activeProjects || this.filterRunningProjects(task)) &&
      this.filterExternalTasks(task, includeExternal)
    )
  }

  /**
   * Returns true is a given task is a subtask which belongs to a task that complies with the assigned filter options
   * @param {Task} task
   * @param {AssignedFilterOptions} options
   * @returns {boolean}
   */
  private isParentTaskAssigned(task: Task, options: AssignedFilterOptions): boolean {
    if (!task.parentId) {
      return false
    }

    const parentTask = this.getTask(task.parentId)

    if (!parentTask) {
      return false
    }

    return this.filterAssignedTasks(parentTask, { ...options, includeSubtasks: false })
  }

  /**
   * Gets the private tasks
   * @param {BaseFilterOptions} [filterOptions={}]
   * @returns {Signal<Task[]>}
   */
  queryPrivateTasks(filterOptions: BaseFilterOptions = {}): Signal<Task[]> {
    const currentUser = this.userQuery.getCurrentUser()

    return this.queryAll({
      filterBy: task =>
        task.baseType === 'private' &&
        (!filterOptions.assigned || this.assignedFilter(task, currentUser)) &&
        (!filterOptions.nonDone || this.nonDoneFilter(task)) &&
        taskSearchFilter(task, filterOptions.searchQuery),
      sortBy: filterOptions?.sortByOrder ? (taskA, taskB) => taskA.order - taskB.order : 'name',
      sortByOrder: Order.ASC
    })
  }

  /**
   * Gets the private tasks assigned to others
   * @returns {Signal<Task[]>}
   */
  queryPrivateTasksAssignedToOthers(): Signal<Task[]> {
    const currentUser = this.userQuery.getCurrentUser()

    return this.queryAll({
      filterBy: task => {
        task = new Task(task)
        return task.baseType === 'private' && !task.isUserAssigned(currentUser.id)
      }
    })
  }

  /**
   * Gets the project tasks by project Id
   * @param {string} projectId
   * @param {FilterOptions} [filterOptions={}]
   * @returns {Signal<Task[]>}
   */
  queryByProject(projectId: string, filterOptions: FilterOptions = {}): Signal<Task[]> {
    const currentUser = this.userQuery.getCurrentUser()

    return this.queryAll({
      filterBy: task =>
        task.projectId === projectId &&
        (!filterOptions.excludeSubtasks || !task.parentId) &&
        (!filterOptions.assigned || this.assignedFilter(task, currentUser)) &&
        (!filterOptions.nonDone || this.nonDoneFilter(task)) &&
        (!filterOptions.filterOldDoneTasks || this.filterOldDoneTasks(task)) &&
        this.filterExternalTasks(task, filterOptions.includeExternal) &&
        taskSearchFilter(task, filterOptions.searchQuery),
      sortBy: 'name',
      sortByOrder: Order.ASC
    })
  }

  /**
   * Gets the tasks time can be tracked on when no project is chosen: tasks of projects that are not closed,
   * and private tasks when they are included.
   * @param {{ includePrivate?: boolean; nonDone?: boolean; }} [options={}]
   * @param {boolean} [options.includePrivate=false] True to include private tasks
   * @param {boolean} [options.nonDone=false] True to filter out done tasks
   * @returns {Signal<Task[]>}
   */
  queryTrackableTasks(options: { includePrivate?: boolean; nonDone?: boolean } = {}): Signal<Task[]> {
    return this.queryAll({
      filterBy: task => {
        const isPrivateTask = task.baseType === 'private'
        const isIncludedPrivateTask = isPrivateTask && !!options.includePrivate
        const isOpenProjectTask = !isPrivateTask && !!task.projectId && task.project?.projectStatus?.type !== 'closed'
        const isAllowedByDoneFilter = !options.nonDone || this.nonDoneFilter(task)

        return (isIncludedPrivateTask || isOpenProjectTask) && isAllowedByDoneFilter
      },
      sortBy: 'name',
      sortByOrder: Order.ASC
    })
  }

  /**
   * Gets the tasks for a given user which are shown in the planner overview/timeline
   * @param {{ userIds: string[]; startDate: Date; endDate: Date; showDoneTasks?: boolean; showTasksOfDoneProjects?: boolean; showRecurringTasks?: boolean; }} args
   * @returns {Signal<Task[]>}
   */
  queryTasksByUserForPlannerOverview(args: {
    userIds: string[]
    startDate: Date
    endDate: Date
    showDoneTasks?: boolean
    showTasksOfDoneProjects?: boolean
    showRecurringTasks?: boolean
  }): Signal<Task[]> {
    const {
      userIds,
      startDate,
      endDate,
      showDoneTasks = false,
      showTasksOfDoneProjects = true,
      showRecurringTasks = true
    } = args

    const acceptedProjectStatus = ['not-started', 'progress']

    if (showTasksOfDoneProjects) {
      acceptedProjectStatus.push('closed')
    }

    const filterCondition = (task: Task) =>
      task.dueOn &&
      task.startOn &&
      userIds.some(userId => task.isUserAssigned(userId)) &&
      (task.baseType === 'private' ||
        this.projectFilterByStatusGroup(task, acceptedProjectStatus as ProjectStatusType[])) &&
      (this.showDoneFilter(task, showDoneTasks) || task.justCheckedOff) &&
      this.showRecurringFilter(task, showRecurringTasks) &&
      areDatesOverlapping({ start: task.startOn, end: task.dueOn }, { start: startDate, end: endDate })

    return this.queryAll({
      filterBy: task => {
        task = new Task(task)
        return filterCondition(task)
      }
    })
  }

  /**
   * Gets the tasks of a project which are within a date range
   * @param {{ projectId: string; dateRange: DateRange; includeExternal?: boolean; showDoneTasks?: boolean; showTasksOfDoneProjects?: boolean; showRecurringTasks?: boolean; }} args
   * @returns {Signal<Task[]>}
   * @param {string} args.projectId
   * @param {DateRange} args.dateRange
   * @param {boolean} args.includeExternal
   * @param {boolean} args.showDoneTasks
   * @param {boolean} args.showTasksOfDoneProjects
   * @param {boolean} args.showRecurringTasks
   */
  queryProjectTasksInDateRange(args: {
    projectId: string
    dateRange: DateRange
    includeExternal?: boolean
    showDoneTasks?: boolean
    showTasksOfDoneProjects?: boolean
    showRecurringTasks?: boolean
  }): Signal<Task[]> {
    const {
      projectId,
      dateRange,
      includeExternal = false,
      showTasksOfDoneProjects = true,
      showDoneTasks = true,
      showRecurringTasks = true
    } = args

    const filterCondition = (task: Task) =>
      task.dueOn &&
      task.startOn &&
      task.projectId === projectId &&
      this.filterExternalTasks(task, includeExternal) &&
      this.showDoneFilter(task, showDoneTasks) &&
      this.showTasksOfDoneProjectsFilter(task, showTasksOfDoneProjects) &&
      this.showRecurringFilter(task, showRecurringTasks) &&
      areDatesOverlapping(
        { start: task.startOn, end: task.dueOn },
        { start: dateRange.startDate, end: dateRange.endDate }
      )

    return this.queryAll({
      filterBy: task => {
        task = new Task(task)
        return filterCondition(task)
      }
    })
  }

  /**
   * Returns all tasks which have a certain dependency as predecessor or successor
   * @param {string} depId
   * @returns {Signal<Task[]>}
   */
  queryByDependencyId(depId: string): Signal<Task[]> {
    return this.queryAll({
      filterBy: task =>
        (task.successors && task.successors.some(dep => dep.id === depId)) ||
        (task.predecessors && task.predecessors.some(dep => dep.id === depId))
    })
  }

  /**
   * Returns all predecessor tasks for a given task
   * @param {string} taskId
   * @returns {Signal<Task[]>}
   */
  queryPredecessorTasks(taskId: string): Signal<Task[]> {
    const task = this.queryTask(taskId)

    return computed(() => {
      if (!task()?.predecessors?.length) {
        return []
      }

      const predecessorTaskIds = task().predecessors.map(dep => dep.taskId)
      const predecessorTasks = this.queryTasks(predecessorTaskIds)

      return predecessorTasks()
    })
  }

  /**
   * Gets a task list's tasks
   * @param {string} taskListId
   * @returns {Signal<Task[]>}
   */
  queryByTaskList(taskListId: string): Signal<Task[]> {
    return this.queryAll({
      filterBy: task => task.lists?.some(list => list.id === taskListId),
      sortBy: (taskA, taskB) =>
        taskA.lists.find(list => list.id === taskListId).orderOfTask -
        taskB.lists.find(list => list.id === taskListId).orderOfTask
    })
  }

  /**
   * Selects a task list's tasks
   * @param {string} taskListId
   * @returns {Task[]}
   */
  getByTaskList(taskListId: string): Task[] {
    const tasks = this.getAll({
      filterBy: task => task.lists?.some(list => list.id === taskListId)
    })

    return this.mapEntities(tasks)
  }

  /**
   * Gets tasks by task status
   * @param {string} taskStatusId
   * @returns {Task[]}
   */
  getByTaskStatus(taskStatusId: string): Task[] {
    const tasks = this.getAll({
      filterBy: task => task.taskStatusId === taskStatusId
    })

    return this.mapEntities(tasks)
  }

  /**
   * Gets task by id
   * @param {string} id
   * @returns {Signal<Task>}
   */
  queryTask(id: string): Signal<Task> {
    return this.queryEntity(id)
  }

  /**
   * Gets task by id
   * @param {string} id
   * @returns {Task}
   */
  getTask(id: string): Task {
    const task = this.getEntity(id)
    return this.mapEntity(task)
  }

  /**
   * Queries the tasks by ids
   * @param {string[]} ids
   * @returns {Signal<Task[]>}
   */
  queryTasks(ids: string[]): Signal<Task[]> {
    return computed(() => {
      const tasks = this.queryMany(ids)
      return tasks()
    })
  }

  /**
   * Gets tasks by ids
   * @param {string[]} ids
   * @returns {Task[]}
   */
  getTasks(ids: string[]): Task[] {
    const tasks = this.getAll({ filterBy: task => ids.includes(task.id) })
    return this.mapEntities(tasks)
  }

  /**
   * Gets a task by its taskIdentifier
   * @param {string} taskIdentifier
   * @param {boolean} [getExternal=false]
   * @returns {Task | null}
   */
  getTaskByIdentifier(taskIdentifier: string, getExternal = false): Task | null {
    const matchingTask = this.getAll().find(
      t => t.taskIdentifier?.toUpperCase() === taskIdentifier.toUpperCase() && t.isExternal === getExternal
    )

    return matchingTask ? this.mapEntity(matchingTask) : null
  }

  /**
   * Gets assigned tasks which are due next
   * For project tasks, only select those from running projects
   * @param {{ activeProjects?: boolean; includeSubtasks?: boolean; includeExternal?: boolean; }} [options={}]
   * @param options.activeProjects True to select only project tasks from active projects (in progress)
   * @param options.includeSubtasks Includes subtasks always if parent task is part of the filter, even when the subtask doesn't meet the filter criteria
   * @param options.includeExternal Includes tasks which are from connected projects that belong to another workspace
   * @returns {Signal<Task[]>}
   */
  queryNextDueAssignedTasks({ activeProjects = true, includeSubtasks = true, includeExternal = false } = {}): Signal<
    Task[]
  > {
    const currentUser = this.userQuery.queryCurrentUser()
    const now = new Date()

    return this.queryAll({
      filterBy: task => {
        return this.filterNextDueAssignedTasks(task, currentUser(), {
          activeProjects,
          includeSubtasks,
          includeExternal
        })
      },
      sortBy: (taskA, taskB) => {
        // sorting: by due date (extracted from Task#dueIn)
        const taskADueOn = taskA.dueOn ? differenceInMinutes(new Date(taskA.dueOn), now) : 999999999
        const taskBDueOn = taskB.dueOn ? differenceInMinutes(new Date(taskB.dueOn), now) : 999999999

        return taskADueOn - taskBDueOn
      }
    })
  }

  /**
   * Returns true if task is assigned to the current user and is due next
   * @param {Task} task
   * @param {User} currentUser
   * @param {{ activeProjects?: boolean; includeSubtasks?: boolean; includeExternal?: boolean; }} [options={}]
   * @returns {boolean}
   */
  private filterNextDueAssignedTasks(
    task: Task,
    currentUser: User,
    { activeProjects = true, includeSubtasks = true, includeExternal = false } = {}
  ): boolean {
    if (includeSubtasks && this.isParentNextDueAssignedTask(task, currentUser, activeProjects, includeExternal)) {
      return true
    }

    return (
      task.taskStatus &&
      (task.justCheckedOff || task.taskStatus.type !== 'done') &&
      this.filterExternalTasks(task, includeExternal) &&
      (!activeProjects || this.filterRunningProjects(task)) &&
      task.assignees?.some(({ id }) => id === currentUser?.id)
    )
  }

  /**
   * Returns true if task has parent which is assigned to the current user and is due next
   * @param {Task} task
   * @param {User} currentUser
   * @param {boolean} [activeProjects=true]
   * @param {boolean} [includeExternal=false]
   * @returns {boolean}
   */
  private isParentNextDueAssignedTask(
    task: Task,
    currentUser: User,
    activeProjects = true,
    includeExternal = false
  ): boolean {
    if (!task.parentId) {
      return false
    }

    const parentTask = this.getEntity(task.parentId)

    if (!parentTask) {
      return false
    }

    return this.filterNextDueAssignedTasks(parentTask, currentUser, {
      activeProjects,
      includeSubtasks: false,
      includeExternal
    })
  }

  /**
   * Gets tasks checked as done
   * @param {boolean} [includeExternal=false]
   * @returns {Task[]}
   */
  getCheckedTasks(includeExternal = false): Task[] {
    const tasks = this.getAll({
      filterBy: task => task.justCheckedOff && this.filterExternalTasks(task, includeExternal)
    })

    return this.mapEntities(tasks)
  }

  /**
   * Selects all tasks according to the searchQuery
   * This is used for global search
   * @param {Injector} injector
   * @param {string} searchQuery
   * @param {number} limit
   * @param {boolean} [includeExternal=false]
   * @returns {Observable<Task[]>}
   * @deprecated
   */
  searchTasks(injector: Injector, searchQuery: string, limit?: number, includeExternal = false): Observable<Task[]> {
    return this.selectAll(injector, {
      limitTo: limit,
      filterBy: task =>
        (searchQuery === '*' || (task.name && task.name.toLowerCase().includes(searchQuery.toLowerCase()))) &&
        this.filterExternalTasks(task, includeExternal),
      sortBy: (taskA, taskB) => {
        // Set scores
        taskA.score = taskA.score ? taskA.score : 0
        taskB.score = taskB.score ? taskB.score : 0

        // if the scores are different, use them to order (desc). Otherwise, order by name (asc)
        if (taskA.score !== taskB.score) {
          return taskB.score - taskA.score
        } else {
          const taskAname = taskA.name || ''
          const taskBname = taskB.name || ''

          return taskAname.localeCompare(taskBname)
        }
      }
    }).pipe(
      // Put done tasks to the end of the array
      map(tasks => {
        const doneTask = tasks.filter(task => task.taskStatus && task.taskStatus.type === 'done')
        const nonDoneTask = tasks.filter(task => !task.taskStatus || task.taskStatus.type !== 'done')

        return this.mapEntities([...nonDoneTask, ...doneTask])
      })
    )
  }

  /**
   * Gets the subtasks by parent task Id
   * @param {string} parentId
   * @returns {Signal<Task[]>}
   */
  queryByParentId(parentId: string): Signal<Task[]> {
    return this.queryAll({
      filterBy: task => task.parentId === parentId,
      sortBy: 'subtaskOrder',
      sortByOrder: Order.ASC
    })
  }

  /**
   * Gets the subtasks by parent task Id
   * @param {string} parentId
   * @returns {Task[]}
   */
  getByParentId(parentId: string): Task[] {
    const tasks = this.getAll({
      filterBy: task => task.parentId === parentId,
      sortBy: 'subtaskOrder',
      sortByOrder: Order.ASC
    })

    return this.mapEntities(tasks)
  }

  /**
   * Gets the subtasks that belong to any of the given parentIds
   * @param {string[]} [parentIds=[]]
   * @returns {Signal<Task[]>}
   */
  queryByParentIds(parentIds: string[] = []): Signal<Task[]> {
    return this.queryAll({
      filterBy: task => parentIds.includes(task.parentId),
      sortBy: 'subtaskOrder',
      sortByOrder: Order.ASC
    })
  }

  /**
   * Gets subtasks grouped by their parent task ID for a given project
   * @param {string} projectId
   * @returns {Signal<Map<string, Task[]>>}
   */
  querySubtasksByProject(projectId: string): Signal<Map<string, Task[]>> {
    return computed(() => {
      const subtasks = this.queryAll({
        filterBy: task => task.projectId === projectId && !!task.parentId,
        sortBy: 'subtaskOrder',
        sortByOrder: Order.ASC
      })

      const mappedSubtasks = subtasks()
      const subtasksByParentId = new Map<string, Task[]>()

      mappedSubtasks.forEach(task => {
        if (subtasksByParentId.has(task.parentId)) {
          subtasksByParentId.get(task.parentId).push(task)
        } else {
          subtasksByParentId.set(task.parentId, [task])
        }
      })

      return subtasksByParentId
    })
  }

  /**
   * Gets the maximum order for private tasks
   * @returns {Signal<number>}
   */
  queryPrivateTaskMaxOrder(): Signal<number> {
    return computed(() => {
      const tasks = this.queryPrivateTasks()
      return Math.max(...tasks().map(task => task.order), 0)
    })
  }

  /**
   * Gets the visible columns in list according to screen size
   * When is not widescreen the array is used to determine which actions are visible
   * @param {'list' | 'board' | 'widget'} view
   * @param {'projectTask' | 'planner' | 'myTasks'} [listEntity='myTasks']
   * @param {string} projectId
   * @returns {Signal<TaskColumn[]>}
   */
  queryColumns(
    view: 'list' | 'board' | 'widget',
    listEntity: 'projectTask' | 'planner' | 'myTasks' = 'myTasks',
    projectId?: string
  ): Signal<TaskColumn[]> {
    const projectLargeListDefault = this.getProjectLargeListDefaultColumns(projectId)

    // This list should have a max of 6 items
    const myTasksLargeListDefault = [
      TaskColumn.TaskKey,
      TaskColumn.IsPrio,
      TaskColumn.Projects,
      TaskColumn.Tags,
      TaskColumn.Assignee,
      TaskColumn.TrackedDuration,
      TaskColumn.DueOn
    ]

    const taskWidgetListDefault = [
      TaskColumn.IsPrio,
      TaskColumn.Tags,
      TaskColumn.CommentCount,
      TaskColumn.Assignee,
      TaskColumn.Recurrency,
      TaskColumn.TrackedDuration,
      TaskColumn.PlannedEffort,
      TaskColumn.DueOn,
      TaskColumn.TaskKey
    ]

    const calendarTaskListDefault = [
      TaskColumn.IsPrio,
      TaskColumn.Tags,
      TaskColumn.CommentCount,
      TaskColumn.Assignee,
      TaskColumn.Recurrency,
      TaskColumn.TrackedDuration,
      TaskColumn.PlannedEffort,
      TaskColumn.DueOn,
      TaskColumn.TaskKey
    ]

    const smallListActions = [
      TaskColumn.IsPrio,
      TaskColumn.Lists,
      TaskColumn.Tags,
      TaskColumn.CommentCount,
      TaskColumn.Assignee,
      TaskColumn.Recurrency,
      TaskColumn.TrackedDuration,
      TaskColumn.PlannedEffort,
      TaskColumn.DueOn,
      TaskColumn.TaskKey
    ]

    const boardActions = [
      TaskColumn.IsPrio,
      TaskColumn.Lists,
      TaskColumn.Tags,
      TaskColumn.CommentCount,
      TaskColumn.Assignee,
      TaskColumn.TrackedDuration,
      TaskColumn.PlannedEffort,
      TaskColumn.DueOn,
      TaskColumn.TaskKey
    ]

    return computed(() => {
      const mainContentSize = this.browserService.mainContentSize()
      const isWidescreen = mainContentSize.width >= TASK_LIST_WIDE_THRESHOLD

      const projectTaskListSettings = this.settingsQuery.queryProjectTaskListSetting(projectId)
      const isListWideWidth = projectTaskListSettings()?.isListWideWidth

      const listColumns = this.settingsQuery.queryListColumns(listEntity, projectId)
      const largeListColumns = listColumns()?.largeList as TaskColumn[]

      let columns: TaskColumn[] = []

      switch (listEntity) {
        case 'projectTask':
          columns = isWidescreen && isListWideWidth ? largeListColumns || projectLargeListDefault : smallListActions
          break
        case 'planner':
          columns = calendarTaskListDefault
          break
        default:
          columns = isWidescreen ? largeListColumns || myTasksLargeListDefault : smallListActions
      }

      if (view === 'board') {
        columns = boardActions
      }

      if (view === 'widget') {
        columns = taskWidgetListDefault
      }

      return columns
    })
  }

  /**
   * Filter used to get running project tasks (projects in progress or subscription)
   * @param {Task} task
   * @returns {boolean}
   */
  private filterRunningProjects(task: Task): boolean {
    // We should only access properties that are available in the task POJO and not instance methods
    if (task.baseType === 'private') {
      return true
    }

    return task.project && task.project.projectStatus && task.project.projectStatus.type === 'progress'
  }

  /**
   * Filter projectes by a group of status
   * @param {Task} task
   * @param {ProjectStatusType[]} statusGroup
   * @returns {boolean}
   */
  private projectFilterByStatusGroup(task: Task, statusGroup: ProjectStatusType[]): boolean {
    return task.project && task.project.projectStatus && statusGroup.includes(task.project.projectStatus.type)
  }

  /**
   * Returns true if the task is not done or if the task
   * was checked to done in the last day.
   * @param {Task} task
   * @returns {boolean}
   */
  private filterRecentDoneTasks(task: Task): boolean {
    if (task && task.taskStatus) {
      return task.taskStatus.type !== 'done' || task.closedOn >= addDays(new Date(), -1)
    }

    return false
  }

  /**
   * Filter done tasks older than 30 days
   * @param {Task} task
   * @returns {boolean}
   */
  private filterOldDoneTasks(task: Task): boolean {
    if (task.taskStatus?.type !== 'done') {
      return true
    }

    return task.justCheckedOff || (task.closedOn && differenceInDays(new Date(), task.closedOn) < 30)
  }

  /**
   * Filter used to exclude done tasks
   * @param {Task} task
   * @returns {boolean}
   */
  private nonDoneFilter(task: Task): boolean {
    return task.taskStatus && task.taskStatus.type !== 'done'
  }

  /**
   * Filter used to get assigned tasks
   * @param {Task} task
   * @param {User} currentUser
   * @returns {boolean}
   */
  private assignedFilter(task: Task, currentUser: User): boolean {
    return new Task(task).isUserAssigned(currentUser.id)
  }

  /**
   * Returns whether the task should be included according to the includeExternal flag
   * @param {Task} task
   * @param {boolean} includeExternal
   * @returns {boolean}
   */
  private filterExternalTasks(task: Task, includeExternal: boolean): boolean {
    return includeExternal || !task.project?.isExternal
  }

  /**
   * Returns false if the task is done and the condition is true
   * @param {Task} task
   * @param {boolean} condition
   * @returns {boolean}
   */
  private showDoneFilter(task: Task, condition: boolean): boolean {
    // We should only access properties that are available in the task POJO and not instance methods
    return task.taskStatus?.type === 'done' ? condition : true
  }

  /**
   * Returns false if the task is recurring and the condition is true
   * @param {Task} task
   * @param {boolean} condition
   * @returns {boolean}
   */
  private showRecurringFilter(task: Task, condition: boolean): boolean {
    return task.isRecurring ? condition : true
  }

  /**
   * Returns false if the task is in a done project and the condition is true
   * @param {Task} task
   * @param {boolean} condition
   * @returns {boolean}
   */
  private showTasksOfDoneProjectsFilter(task: Task, condition: boolean): boolean {
    // We should only access properties that are available in the task POJO and not instance methods

    const isProjectDone =
      task.project?.projectStatus?.type === 'closed' || task.project?.projectStatus?.type === 'stuck'
    return isProjectDone ? condition : true
  }

  /**
   * Gets the default columns for large list view.
   * If the project was created after the release of the custom fields, the default columns are the static ones.
   * Otherwise, the default columns are the ones selected by the user before the release of custom fields.
   * @param {string} projectId
   * @returns {TaskColumn[]}
   */
  private getProjectLargeListDefaultColumns(projectId: string): TaskColumn[] {
    const projectLargeListDefault = [
      TaskColumn.TaskKey,
      TaskColumn.IsPrio,
      TaskColumn.ChecklistItems,
      TaskColumn.Tags,
      TaskColumn.Assignee,
      TaskColumn.TrackedDuration,
      TaskColumn.DueOn
    ]

    const project = this.projectQuery.queryProject(projectId)
    const customFieldReleaseDate = new Date('2024-08-24')

    if (new Date(project()?.createdOn) > customFieldReleaseDate) {
      return projectLargeListDefault
    }

    const selectedListColumns = this.settingsQuery.queryListColumns('projectTask')

    return (selectedListColumns()?.largeList as TaskColumn[]) || projectLargeListDefault
  }
}
